import React, { useEffect, useRef, useState, useCallback } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { useSelector } from "react-redux";
import { API } from "api/axios.config";

const textField = "matterRef";
const keyField = "matterId";
const pageSize = 30;

const emptyItem = {
  [textField]: "loading ...",
};

const loadingData = [];

while (loadingData.length < pageSize) {
  loadingData.push({ ...emptyItem });
}

const MattersCombobox = ({
  value,
  onChangematter,
  controlled = false,
  component,
  ...props
}) => {
  const dataCaching = useRef([]);
  const pendingRequest = useRef(undefined);
  const requestStarted = useRef(false);
  const skipRef = useRef(0);
  const comboBoxRef = useRef(null);

  const [data, setData] = useState([]);
  const [internalValue, setInternalValue] = useState(null);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const itemRender = (li, itemProps) => {
    const { dataItem } = itemProps;
    const content = (
      <div className="d-flex justify-content-center align-items-center flex-column w-100">
        <div
          className="d-flex flex-column justify-content-center ms-2 w-100 my-2 ps-2"
          style={{
            backgroundColor: "#F8F8F8",
            borderRadius: "5px",
            padding: "6px",
          }}>
          <div className="d-flex">
            <span className="fw-semibold fs-sm">{dataItem.matterRef}</span>
            <span className="ms-2 fw-semibold fs-sm text-decoration-underline">
              {dataItem.feeEarnerName}
            </span>
          </div>
          <span className="fs-sm">{dataItem.details}</span>
        </div>
        <hr
          className="ms-1 m-0"
          style={{
            width: "95%",
            border: "0.5px solid #E1E1E1",
          }}
        />
      </div>
    );
    return React.cloneElement(li, li.props, content);
  };
  const requestData = useCallback(async (skip = 0, filter = "") => {
    if (requestStarted.current) {
      clearTimeout(pendingRequest.current);
      pendingRequest.current = setTimeout(() => requestData(skip, filter), 50);
      return;
    }

    requestStarted.current = true;
    setLoading(true);

    try {
      const query = new URLSearchParams({
        ...(filter && { search: filter }),
        page: Math.floor(skip / pageSize) + 1,
        limit: pageSize,
        active: true,
      }).toString();

      const { data: response } = await API.get(`/matters?${query}`);

      const items = response.data.map((item, i) => ({
        [keyField]: item.matterId,
        [textField]: item.matterRef,
        feeEarnerRef: item.feeEarnerRef,
        feeEarnerName: item.feeEarnerName,
        ...item,
      }));

      items.forEach((item, i) => {
        dataCaching.current[i + skip] = item;
      });

      if (skip === skipRef.current) {
        setData(items);
      }

      setTotal(response.total ?? response.data.length);
    } catch (error) {
      console.error("Failed to fetch matters:", error);
      setData([]);
    } finally {
      requestStarted.current = false;
      setLoading(false);
    }
  }, []);

  const resetCache = useCallback(() => {
    dataCaching.current.length = 0;
  }, []);

  useEffect(() => {
    // Don't make API calls if the component is disabled (view/edit mode)
    if (!props.disabled) {
      requestData(0, filter);
    }
    return () => {
      resetCache();
    };
  }, [filter, requestData, props.disabled]);

  useEffect(() => {
    if (props.reset) {
      setInternalValue(null);
      resetCache();
      setFilter("");
      requestData(0, "");
    }
  }, [props.reset]);

  // Always sync props.value to internal state (works for both controlled and uncontrolled)
  useEffect(() => {
    setInternalValue(value ?? null);
  }, [value]);

  const shouldRequestData = useCallback((skip) => {
    for (let i = 0; i < pageSize; i++) {
      if (!dataCaching.current[skip + i]) return true;
    }
    return false;
  }, []);

  const getCachedData = useCallback((skip) => {
    let dataTemp = [];

    for (let i = 0; i < pageSize; i++) {
      dataTemp.push(dataCaching.current[i + skip] || { ...emptyItem });
    }

    return dataTemp;
  }, []);

  const pageChange = useCallback(
    (event) => {
      const newSkip = event.page.skip;
      if (isNaN(newSkip)) return;

      if (shouldRequestData(newSkip)) {
        requestData(newSkip, filter);
      }

      const pageData = getCachedData(newSkip);
      setData(pageData);
      skipRef.current = newSkip;
    },
    [getCachedData, requestData, shouldRequestData, filter]
  );

  const onFilterChange = useCallback(
    (event) => {
      const newFilter = event.filter.value || "";
      resetCache();
      setData(loadingData);
      skipRef.current = 0;
      setFilter(newFilter);
      requestData(0, newFilter);
      props.onFilterChange?.(event);
    },
    [props]
  );

  const performManualSearch = useCallback(
    (searchTerm) => {
      if (!searchTerm?.trim()) {
        return;
      }
      const trimmed = searchTerm.trim();
      setFilter(trimmed);
      resetCache();
      setData(loadingData);
      skipRef.current = 0;
      setLoading(true);
      requestData(0, trimmed);
    },
    [resetCache, requestData]
  );

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter") {
        const comboInput = comboBoxRef.current?.element?.querySelector("input");
        if (e.target === comboInput) {
          const currentValue = e.target.value?.trim();
          // Only handle Enter manually if Kendo hasn’t loaded results yet
          const shouldHandleManually = !!currentValue && loading;

          if (shouldHandleManually) {
            e.preventDefault();
            e.stopPropagation();
            performManualSearch(currentValue);
          }
        }
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, [data?.length, loading]);

  const onChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      // Only update internal state when uncontrolled (let parent control when controlled=true)
      if (!controlled) {
        setInternalValue(newValue);
      }
      onChangematter?.(event);
    },
    [controlled, onChangematter]
  );

  const styleObject = {
    width: props.width != null ? `${props.width}px` : "300px",
    minWidth: 0,
    borderRadius: props.borderRadius || undefined,
  };

  return (
    <ComboBox
      ref={comboBoxRef}
      loading={loading}
      required={props.required}
      data={data}
      value={internalValue}
      onChange={onChange}
      dataItemKey={keyField}
      disabled={props.disabled}
      textField={textField}
      placeholder="Select Matter"
      filterable
      onFilterChange={onFilterChange}
      virtual={{
        pageSize,
        skip: skipRef.current,
        total,
      }}
      onPageChange={pageChange}
      style={{
        ...styleObject,
        ...props.style,
        border: props.error && "1px solid red",
      }}
      itemRender={itemRender}
      className={(props.className ?? "") + " px-2"}
    />
  );
};

export default MattersCombobox;
